// src/components/Sidebar.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaUser,
  FaUsers,
  FaClipboardList,
  FaCogs,
  FaUserGraduate,
  FaCalendarCheck,
  FaSms,
  FaMoneyBillWave,
  FaChartBar,
  FaEllipsisH,
} from "react-icons/fa";

/**
 * Sidebar with separate light bars for each main section.
 * Accepts prop isOpen (optional) to collapse/hide (Layout passes it).
 */
export default function Sidebar({ isOpen = true }) {
  const [openDropdown, setOpenDropdown] = useState(null);

  const toggle = (name) => setOpenDropdown(openDropdown === name ? null : name);

  return (
    <aside
      className={`bg-gray-100 border-r h-screen overflow-auto transition-all ${
        isOpen ? "w-72" : "w-16"
      }`}
    >
      {/* Top login detail */}
      <div className="bg-gray-800 text-white p-4 text-center font-semibold">
        Logged in as: <div className="text-sm mt-1">Admin</div>
      </div>

      {/* Back to Dashboard (always visible under login header) */}
      <div className="bg-white p-3 border-b flex items-center">
        <Link to="/dashboard" className="text-gray-700 font-medium">
          ← Back to Dashboard
        </Link>
      </div>

      <nav className="p-2 space-y-2">
        {/* 1 */}
        <div className="bg-white p-3 rounded shadow-sm">
          <Link to="/profile" className="flex items-center gap-3 text-gray-800">
            <FaUser /> <span>My Profile</span>
          </Link>
        </div>

        {/* 2 */}
        <div className="bg-white p-3 rounded shadow-sm">
          <Link to="/users" className="flex items-center gap-3 text-gray-800">
            <FaUsers /> <span>Manage Users</span>
          </Link>
        </div>

        {/* 3 */}
        <div className="bg-white p-3 rounded shadow-sm">
          <Link to="/enquiries" className="flex items-center gap-3 text-gray-800">
            <FaClipboardList /> <span>Enquiry Management</span>
          </Link>
        </div>

        {/* 4. Master */}
        <div className="bg-white p-3 rounded shadow-sm">
          <div
            onClick={() => toggle("master")}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-center gap-3 text-gray-800">
              <FaCogs /> <span>Master</span>
            </div>
            <div>{openDropdown === "master" ? "▾" : "▸"}</div>
          </div>

          {openDropdown === "master" && (
            <ul className="mt-2 ml-6 space-y-2 text-sm">
              <li><Link to="/enrollment-fee">Enrollment Fee</Link></li>
              <li><Link to="/course-heads">Affiliation / Approval</Link></li>
              <li><Link to="/courses">Manage Courses</Link></li>
              <li><Link to="/subjects">Manage Subject</Link></li>
              <li><Link to="/modules">Manage Module</Link></li>
              <li><Link to="/batches">Manage Batch</Link></li>
              <li><Link to="/tax">Manage Tax</Link></li>
              <li><Link to="/notice">Manage Notice</Link></li>
            </ul>
          )}
        </div>

        {/* 5. Student */}
        <div className="bg-white p-3 rounded shadow-sm">
          <div
            onClick={() => toggle("student")}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-center gap-3 text-gray-800">
              <FaUserGraduate /> <span>Student</span>
            </div>
            <div>{openDropdown === "student" ? "▾" : "▸"}</div>
          </div>

          {openDropdown === "student" && (
            <ul className="mt-2 ml-6 space-y-2 text-sm">
              <li><Link to="/add-student">Add Student</Link></li>
              <li><Link to="/manage-students">Manage Student</Link></li>
            </ul>
          )}
        </div>

        {/* 6. Attendance */}
        <div className="bg-white p-3 rounded shadow-sm">
          <div
            onClick={() => toggle("attendance")}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-center gap-3 text-gray-800">
              <FaCalendarCheck /> <span>Attendance</span>
            </div>
            <div>{openDropdown === "attendance" ? "▾" : "▸"}</div>
          </div>

          {openDropdown === "attendance" && (
            <ul className="mt-2 ml-6 space-y-2 text-sm">
              <li><Link to="/attendance/add">Add Attendance</Link></li>
              <li><Link to="/attendance/mark">Mark Attendance</Link></li>
              <li><Link to="/attendance/manage">Manage Attendance</Link></li>
              <li><Link to="/attendance/module-wise">Module wise</Link></li>
              <li><Link to="/attendance/batch-wise">Batch wise</Link></li>
            </ul>
          )}
        </div>

        {/* 7. SMS */}
        <div className="bg-white p-3 rounded shadow-sm">
          <div
            onClick={() => toggle("sms")}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-center gap-3 text-gray-800">
              <FaSms /> <span>SMS</span>
            </div>
            <div>{openDropdown === "sms" ? "▾" : "▸"}</div>
          </div>

          {openDropdown === "sms" && (
            <ul className="mt-2 ml-6 space-y-2 text-sm">
              <li><Link to="/sms/template">SMS Template</Link></li>
              <li><Link to="/sms/history">SMS History</Link></li>
              <li><Link to="/sms/bulk">Bulk Message</Link></li>
            </ul>
          )}
        </div>

        {/* 8. Manage Expenses */}
        <div className="bg-white p-3 rounded shadow-sm">
          <Link to="/expenses" className="flex items-center gap-3 text-gray-800">
            <FaMoneyBillWave /> <span>Manage Expenses</span>
          </Link>
        </div>

        {/* 9. Report */}
        <div className="bg-white p-3 rounded shadow-sm">
          <div
            onClick={() => toggle("report")}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-center gap-3 text-gray-800">
              <FaChartBar /> <span>Report</span>
            </div>
            <div>{openDropdown === "report" ? "▾" : "▸"}</div>
          </div>

          {openDropdown === "report" && (
            <ul className="mt-2 ml-6 space-y-2 text-sm">
              <li><Link to="/report/students">Students Report</Link></li>
              <li><Link to="/report/collection">Collection Report</Link></li>
              <li><Link to="/report/collection-graph">Collection Graph</Link></li>
              <li><Link to="/report/expense">Expense Report</Link></li>
            </ul>
          )}
        </div>

        {/* 10. Other */}
        <div className="bg-white p-3 rounded shadow-sm mb-4">
          <div
            onClick={() => toggle("other")}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex items-center gap-3 text-gray-800">
              <FaEllipsisH /> <span>Other</span>
            </div>
            <div>{openDropdown === "other" ? "▾" : "▸"}</div>
          </div>

          {openDropdown === "other" && (
            <ul className="mt-2 ml-6 space-y-2 text-sm">
              <li><Link to="/theme">Theme</Link></li>
              <li><Link to="/complaint">Submit Complaint</Link></li>
              <li><Link to="/subscription">Subscription</Link></li>
              <li><Link to="/refer">Refer & Earn</Link></li>
              <li><Link to="/contact">Contact Us</Link></li>
              <li><Link to="/logout">Logout</Link></li>
            </ul>
          )}
        </div>
      </nav>
    </aside>
  );
}
